import React, { useState, useEffect } from "react";
import { AppBar, Tabs, Tab, Typography, Box, ThemeProvider, createTheme, Toolbar } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import EventsTable from "../components/EventsTable";
import EventDetails from "../components/EventDetails";
import EventParticipants from "../components/EventParticipants";
import PoolManagement from "../components/PoolManagement";
import UserProfile from "../components/UserProfile";
import AdminManagement from "../components/AdminManagement"; 
import { useAuth } from "../contexts/AuthContext"; 

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#90caf9",
    },
    background: {
      default: "#121212",
      paper: "#1e1e1e",
    },
    text: {
      primary: "#fff",
      secondary: "#b0bec5",
    },
  },
  typography: {
    fontFamily: "Arial, sans-serif",
    h4: {
      fontWeight: 700,
    },
  },
});

const HomePage = () => {
  const { shareIdentifier } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth(); 
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if user is admin or superadmin (can see admin management)
  const canManageAdmins = user?.role === 'admin' || user?.role === 'superadmin';

  useEffect(() => {
    const fetchEvents = async () => {
      const apiUrl = `${process.env.REACT_APP_API_URL}/events`;
      console.log("Fetching events from:", apiUrl);

      try {
        const response = await fetch(apiUrl);
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        setEvents(data);
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    if (shareIdentifier) {
      setSelectedEvent(shareIdentifier);
      const searchParams = new URLSearchParams(window.location.search);
      const tab = searchParams.get("tab");
  
      if (tab === "participants") {
        setTabIndex(3);
      } else {
        setTabIndex(1);
      }
    }
  }, [shareIdentifier]);

  const handleEventClick = (identifier) => {
    setSelectedEvent(identifier);
    setTabIndex(1);
    navigate(`/eventDetails/${identifier}`);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Box sx={{ width: "100%", height: "100vh", bgcolor: "background.default", color: "text.primary" }}>
        {/* Header with User Profile */}
        <AppBar position="static" sx={{ bgcolor: "#2a2a2a" }}>
          <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h4">
              Admin Dashboard
            </Typography>
            <UserProfile /> 
          </Toolbar>
        </AppBar>

        {/* Tabs */}
        <Tabs
          value={tabIndex}
          onChange={(event, newIndex) => setTabIndex(newIndex)}
          centered
          sx={{ bgcolor: "background.paper" }}
        >
          <Tab label="Events" />
          <Tab label="Event Details" />
          <Tab label="Participants" />
          <Tab label="Pool Management" />
          {canManageAdmins && <Tab label="Admin Users" />}
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ padding: 3, backgroundColor: "background.default", color: "text.primary" }}>
          {tabIndex === 0 && (
            <>
              {loading && <Typography>Loading events...</Typography>}
              {error && <Typography color="error">Error: {error}</Typography>}
              {!loading && !error && <EventsTable events={events} onEventClick={handleEventClick} />}
            </>
          )}
          {tabIndex === 1 && selectedEvent && <EventDetails shareIdentifier={selectedEvent} />}
          {tabIndex === 2 && selectedEvent && <EventParticipants shareIdentifier={selectedEvent} />}
          {tabIndex === 3 && <PoolManagement />}
          {tabIndex === 4 && canManageAdmins && <AdminManagement />}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default HomePage;